import { create } from "zustand";
import { apiClient } from "../lib/api-client";
import { exportVaultKey, getStoredKey } from "../lib/crypto";
import { useAuthStore } from "./auth-store";

const tenantHeaders = () => {
  const { activeTenant } = useAuthStore.getState();
  return activeTenant?.id ? { headers: { "X-Tenant-ID": activeTenant.id } } : undefined;
};

export const NOTIFICATION_MODES = {
  SIMPLE: "simple",
  FULL: "full",
};

export const NOTIFICATION_CHANNELS = [
  { value: "email", label: "E-mail" },
  { value: "telegram", label: "Telegram" },
  { value: "whatsapp", label: "WhatsApp" },
];

export const NOTIFICATION_SCOPES = [
  { value: "payable", label: "Transações a pagar" },
  { value: "receivable", label: "Transações a receber" },
  { value: "card_invoice", label: "Cartões e faturas" },
];

const emptyConsent = {
  enabled: false,
  mode: NOTIFICATION_MODES.SIMPLE,
  scopes: [],
  channels: [],
  lead_days: [3, 1, 0],
  quiet_hours_start: null,
  quiet_hours_end: null,
  timezone: "America/Sao_Paulo",
  grant_active: false,
  grant_stale: false,
  grant_last_used_at: null,
  full_mode_available: false,
};

export const useNotificationStore = create((set, get) => ({
  consent: emptyConsent,
  channels: [],
  isLoading: false,
  error: null,

  fetchConsent: async () => {
    set({ isLoading: true });
    try {
      const [consent, channels] = await Promise.all([
        apiClient.get("/notification-consents", tenantHeaders()),
        apiClient.get("/notification-channels", tenantHeaders()),
      ]);
      set({ consent: { ...emptyConsent, ...consent.data }, channels: channels.data, isLoading: false });
      return { success: true };
    } catch (error) {
      set({
        error: error.response?.data?.detail || "Erro ao carregar notificações",
        isLoading: false,
      });
      return { success: false };
    }
  },

  saveConsent: async (patch) => {
    const current = get().consent;
    const payload = {
      enabled: patch.enabled ?? current.enabled,
      mode: patch.mode ?? current.mode,
      scopes: patch.scopes ?? current.scopes,
      channels: patch.channels ?? current.channels,
      lead_days: patch.lead_days ?? current.lead_days,
      quiet_hours_start: patch.quiet_hours_start ?? current.quiet_hours_start,
      quiet_hours_end: patch.quiet_hours_end ?? current.quiet_hours_end,
      timezone: patch.timezone ?? current.timezone,
    };

    try {
      const response = await apiClient.put("/notification-consents", payload, tenantHeaders());
      set({ consent: { ...emptyConsent, ...response.data }, error: null });
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao salvar notificações";
      set({ error: message });
      return { success: false, error: message };
    }
  },

  /**
   * Cerimônia do modo completo: exporta a chave do cofre já aberta nesta sessão e a
   * entrega uma única vez ao servidor, junto com a senha como prova de presença.
   */
  grantFullMode: async (password) => {
    const key = getStoredKey();
    if (!key) {
      return { success: false, error: "Cofre bloqueado. Faça login novamente." };
    }

    try {
      const vaultKey = await exportVaultKey(key);
      await apiClient.post("/notification-grants", { password, vault_key: vaultKey }, tenantHeaders());
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao autorizar notificações";
      set({ error: message });
      return { success: false, error: message };
    }
  },

  revokeGrant: async () => {
    try {
      await apiClient.delete("/notification-grants", tenantHeaders());
      set((state) => ({
        consent: { ...state.consent, grant_active: false, grant_stale: false, grant_last_used_at: null },
      }));
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao revogar autorização";
      set({ error: message });
      return { success: false, error: message };
    }
  },

  addChannel: async (channelType, destination) => {
    try {
      const response = await apiClient.post(
        "/notification-channels",
        { channel_type: channelType, destination },
        tenantHeaders()
      );
      set((state) => ({
        channels: [...state.channels.filter((c) => c.channel_type !== channelType), response.data],
      }));
      return { success: true, channel: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao cadastrar canal";
      set({ error: message });
      return { success: false, error: message };
    }
  },

  verifyChannel: async (channelId, code) => {
    try {
      const response = await apiClient.post(
        `/notification-channels/${channelId}/verify`,
        { code },
        tenantHeaders()
      );
      set((state) => ({
        channels: state.channels.map((channel) => (channel.id === channelId ? response.data : channel)),
      }));
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Código inválido ou expirado";
      set({ error: message });
      return { success: false, error: message };
    }
  },

  removeChannel: async (channelId) => {
    try {
      await apiClient.delete(`/notification-channels/${channelId}`, tenantHeaders());
      set((state) => ({ channels: state.channels.filter((channel) => channel.id !== channelId) }));
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao remover canal";
      set({ error: message });
      return { success: false, error: message };
    }
  },

  /**
   * Publica o índice de vencimentos (datas e escopo, sem conteúdo). Só o cliente
   * consegue lê-los, então é ele que mantém o índice em dia.
   */
  syncSchedules: async (items) => {
    if (!get().consent.enabled) return { success: false, skipped: true };

    try {
      const response = await apiClient.put("/notification-schedules/sync", { items }, tenantHeaders());
      return { success: true, ...response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  clearNotifications: () => set({ consent: emptyConsent, channels: [], error: null }),
}));
